// Firestore-backed data layer for Collavue.
//
// Layout:
//   users/{uid}/campaigns/{id}
//   users/{uid}/leads/{id}
//   users/{uid}/syncJobs/{id}
//   users/{uid}/replies/{id}
//   users/{uid}/brandVoice/profile
//   users/{uid}/notifications/{id}
//   users/{uid}/creditTransactions/{id}
import {
  collection,
  doc,
  addDoc,
  setDoc,
  getDoc,
  getDocs,
  query,
  orderBy,
  limit,
  where,
  updateDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";
import { db, auth } from "./firebase";

function uid() {
  const u = auth.currentUser?.uid;
  if (!u) throw new Error("Not signed in");
  return u;
}

function userRef(u = uid()) {
  return doc(db, "users", u);
}

// A subscription without an error handler is torn down silently by the SDK,
// which then logs "Uncaught Error in snapshot listener" with no indication of
// which path was refused — the usual symptom of a Firestore rules gap. Every
// listener below routes its errors through here so the console names the
// collection, and callers can render an empty state instead of hanging on a
// spinner forever.
function onListenError(label, onError) {
  return (err) => {
    console.error(
      `[collavue] ${label} listener failed (${err?.code || "unknown"}): ${err?.message || err}`
    );
    if (onError) onError(err);
  };
}

// -------- Campaigns --------
export async function createCampaign(payload) {
  const ref = collection(userRef(), "campaigns");
  const out = await addDoc(ref, {
    ...payload,
    status: payload.status || "active",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { id: out.id };
}

export async function updateCampaign(id, patch) {
  await updateDoc(doc(userRef(), "campaigns", id), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteCampaign(id) {
  await deleteDoc(doc(userRef(), "campaigns", id));
}

export async function getCampaign(id) {
  const snap = await getDoc(doc(userRef(), "campaigns", id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export function subscribeCampaigns(cb, onError) {
  const q = query(
    collection(userRef(), "campaigns"),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onListenError("campaigns", onError)
  );
}

// Live view of a single campaign. The campaign editor needs this rather than a
// one-shot read: the daily digest email's "turn off auto-sync" link and the
// Firestore console both write to this document, and an editor holding a stale
// copy would push the old `autoSyncDaily` back on its next save.
export function subscribeCampaign(id, cb, onError) {
  return onSnapshot(
    doc(userRef(), "campaigns", id),
    (snap) => cb(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    onListenError("campaign", onError)
  );
}

// -------- Leads --------
export function subscribeLeads(filters, cb, onError) {
  const u = uid();
  const base = collection(doc(db, "users", u), "leads");
  const q = filters?.campaignId
    ? query(
        base,
        where("campaignId", "==", filters.campaignId),
        orderBy("createdAt", "desc"),
        limit(200)
      )
    : query(base, orderBy("createdAt", "desc"), limit(200));
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onListenError("leads", onError)
  );
}

export async function updateLeadStatus(id, status) {
  await updateDoc(doc(userRef(), "leads", id), {
    status,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteLead(id) {
  await deleteDoc(doc(userRef(), "leads", id));
}

// Delete every lead generated by a campaign. Used when the user opts to
// remove a campaign's leads alongside the campaign itself. Returns the number
// of leads deleted. Firestore caps a write batch at 500 ops, so we chunk.
export async function deleteLeadsByCampaign(campaignId) {
  const base = collection(userRef(), "leads");
  const snap = await getDocs(query(base, where("campaignId", "==", campaignId)));
  const docs = snap.docs;
  for (let i = 0; i < docs.length; i += 450) {
    const batch = writeBatch(db);
    for (const d of docs.slice(i, i + 450)) batch.delete(d.ref);
    await batch.commit();
  }
  return docs.length;
}

export async function getLead(id) {
  const snap = await getDoc(doc(userRef(), "leads", id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export function subscribeLead(id, cb, onError) {
  return onSnapshot(
    doc(userRef(), "leads", id),
    (snap) => cb(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    onListenError("lead", onError)
  );
}

// -------- Replies --------
export function subscribeReplies(leadId, cb, onError) {
  const u = uid();
  const q = query(
    collection(doc(db, "users", u), "replies"),
    where("leadId", "==", leadId),
    orderBy("createdAt", "desc"),
    limit(20)
  );
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onListenError("replies", onError)
  );
}

// -------- Lead commands (saved lead-assistant searches) --------
// A lead command is a natural-language search the user saved from the lead
// assistant ("find every lead that mentions pricing"). Commands with
// runOnDailySync are re-evaluated against each daily auto-sync's new leads
// server-side (1 credit per command per sync) and reported in the digest email.
export function subscribeLeadCommands(cb, onError) {
  const q = query(
    collection(userRef(), "leadCommands"),
    orderBy("createdAt", "desc"),
    limit(20)
  );
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onListenError("leadCommands", onError)
  );
}

export async function createLeadCommand(payload) {
  const out = await addDoc(collection(userRef(), "leadCommands"), {
    name: payload.name || "",
    prompt: payload.prompt || "",
    campaignId: payload.campaignId || null,
    runOnDailySync: payload.runOnDailySync !== false,
    lastMatchCount: payload.lastMatchCount ?? null,
    lastMatchLeadIds: payload.lastMatchLeadIds || [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { id: out.id };
}

export async function updateLeadCommand(id, patch) {
  await updateDoc(doc(userRef(), "leadCommands", id), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteLeadCommand(id) {
  await deleteDoc(doc(userRef(), "leadCommands", id));
}

// -------- Brand voice --------
export async function saveBrandVoice(profile) {
  await setDoc(
    doc(userRef(), "brandVoice", "profile"),
    { ...profile, updatedAt: serverTimestamp() },
    { merge: true }
  );
}
export async function getBrandVoice() {
  const snap = await getDoc(doc(userRef(), "brandVoice", "profile"));
  return snap.exists() ? snap.data() : null;
}

// -------- Notifications --------
export function subscribeNotifications(cb, onError) {
  const q = query(
    collection(userRef(), "notifications"),
    orderBy("createdAt", "desc"),
    limit(40)
  );
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onListenError("notifications", onError)
  );
}
export async function markNotificationRead(id) {
  await updateDoc(doc(userRef(), "notifications", id), { read: true });
}

// -------- Sync jobs --------
export function subscribeSyncJobs(cb, onError) {
  const q = query(
    collection(userRef(), "syncJobs"),
    orderBy("startedAt", "desc"),
    limit(20)
  );
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onListenError("syncJobs", onError)
  );
}

// -------- Credits --------
export function subscribeCreditTransactions(cb, onError) {
  const q = query(
    collection(userRef(), "creditTransactions"),
    orderBy("createdAt", "desc"),
    limit(40)
  );
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onListenError("creditTransactions", onError)
  );
}

export async function getCreditsOnce() {
  const snap = await getDoc(userRef());
  return Number(snap.data()?.credits || 0);
}

export const COLLAVUE_COSTS = {
  analyze_website: 2,
  generate_campaign: 5,
  manual_sync: 15,
  generate_reply: 1,
  train_brand_voice: 3,
  lead_chat: 1,
  lead_command_run: 1,
};

// Server caps how many saved commands one daily sync evaluates per user.
// Keep in sync with COLLAVUE_MAX_DAILY_COMMANDS in functions/index.js.
export const COLLAVUE_MAX_DAILY_COMMANDS = 5;

// Campaign goals — what the user wants OUT of their leads. Shapes both the
// generated strategy and how the semantic screen grades relevance.
export const CAMPAIGN_GOALS = [
  {
    v: "sales",
    label: "Sales",
    desc: "Find buyers: high-intent posts you can reply to and convert.",
  },
  {
    v: "research",
    label: "Research",
    desc: "Understand your market: pains, opinions, competitor complaints.",
  },
  {
    v: "data",
    label: "Data",
    desc: "Build a dataset: broad coverage of every relevant conversation.",
  },
];

export function campaignGoalLabel(goal) {
  const found = CAMPAIGN_GOALS.find((g) => g.v === goal);
  return found ? found.label : "Sales";
}

// The sources Collavue searches for leads. Order here is the order they're
// offered in every picker and filter. Keep the keys in sync with
// COLLAVUE_SUPPORTED_PLATFORMS in functions/index.js — they're written to the
// campaign's `platforms` array and read back by the sync.
export const LEAD_PLATFORMS = [
  { v: "x", label: "X (Twitter)", short: "X" },
  { v: "linkedin", label: "LinkedIn", short: "LinkedIn" },
  { v: "reddit", label: "Reddit", short: "Reddit" },
];

export const LEAD_PLATFORM_KEYS = LEAD_PLATFORMS.map((p) => p.v);

// Leads written before the platform collapse carry "twitter"; everything
// downstream treats that as "x".
export function normalizePlatformKey(platform) {
  const key = String(platform || "").toLowerCase().trim();
  return key === "twitter" ? "x" : key;
}

export function platformLabel(platform) {
  const key = normalizePlatformKey(platform);
  const found = LEAD_PLATFORMS.find((p) => p.v === key);
  return found ? found.label : platform || "Unknown platform";
}

// A campaign's selected sources, normalized and de-duped. Campaigns created
// before source selection existed (or whose stored value is unusable) fall
// back to every source, which is what the back end searches for them.
export function campaignPlatforms(campaign) {
  const raw = Array.isArray(campaign?.platforms) ? campaign.platforms : [];
  const out = [];
  for (const entry of raw) {
    const key = normalizePlatformKey(entry);
    if (!LEAD_PLATFORM_KEYS.includes(key)) continue;
    if (!out.includes(key)) out.push(key);
  }
  return out.length ? out : [...LEAD_PLATFORM_KEYS];
}

// "X (Twitter), LinkedIn & Reddit" — for prose that names a campaign's sources.
export function campaignPlatformsLabel(campaign) {
  const labels = campaignPlatforms(campaign).map(platformLabel);
  if (labels.length <= 1) return labels[0] || "";
  return `${labels.slice(0, -1).join(", ")} & ${labels[labels.length - 1]}`;
}

// Sync cost scales linearly with the campaign's lookback window:
// 3 hours (0.125 days) → 15 credits, 90 days → 100 credits. Anything
// in between is interpolated and rounded. Keep this in sync with the
// matching helper in functions/index.js.
export const COLLAVUE_SYNC_COST_MIN = 15;
export const COLLAVUE_SYNC_COST_MAX = 100;
export const COLLAVUE_SYNC_MIN_DAYS = 3 / 24;
export const COLLAVUE_SYNC_MAX_DAYS = 90;
// When a campaign has no explicit lookback window the back end falls back to a
// 7-day search (collavueNormalizeSyncDateRangeDays in functions/index.js), so
// the displayed cost must use the same default or it under-reports the charge.
export const COLLAVUE_SYNC_DEFAULT_DAYS = 7;

// Reddit is searched through redditapis.com, which meters every read, and the
// number of reads a sync makes tracks the lookback window. So a Reddit-enabled
// sync carries a surcharge that scales the same way the base cost does — 5
// credits at the 3-hour floor, 12 at 90 days. X and LinkedIn add nothing.
// Keep in sync with COLLAVUE_REDDIT_SURCHARGE_* in functions/index.js.
export const COLLAVUE_REDDIT_SURCHARGE_MIN = 5;
export const COLLAVUE_REDDIT_SURCHARGE_MAX = 12;

function interpolateByWindow(dateRangeDays, min, max) {
  const raw = Number(dateRangeDays);
  const days =
    Number.isFinite(raw) && raw > 0
      ? Math.min(COLLAVUE_SYNC_MAX_DAYS, Math.max(COLLAVUE_SYNC_MIN_DAYS, raw))
      : COLLAVUE_SYNC_DEFAULT_DAYS;
  const span = COLLAVUE_SYNC_MAX_DAYS - COLLAVUE_SYNC_MIN_DAYS;
  const ratio = (days - COLLAVUE_SYNC_MIN_DAYS) / span;
  return min + ratio * (max - min);
}

/** Credits a Reddit-enabled sync adds on top of the base cost. */
export function collavueRedditSurcharge(dateRangeDays) {
  return Math.round(
    interpolateByWindow(
      dateRangeDays,
      COLLAVUE_REDDIT_SURCHARGE_MIN,
      COLLAVUE_REDDIT_SURCHARGE_MAX
    )
  );
}

/**
 * Total credits for one sync of this campaign.
 *
 * `platforms` is the campaign's selected sources. Omitting it prices every
 * source, which is what the back end searches for a campaign with no usable
 * selection — so the displayed cost can never come in under the charge.
 */
export function collavueSyncCost(dateRangeDays, platforms) {
  const base = Math.round(
    interpolateByWindow(
      dateRangeDays,
      COLLAVUE_SYNC_COST_MIN,
      COLLAVUE_SYNC_COST_MAX
    )
  );
  const selected = Array.isArray(platforms) ? platforms : LEAD_PLATFORM_KEYS;
  const usesReddit = selected.some(
    (p) => normalizePlatformKey(p) === "reddit"
  );
  return base + (usesReddit ? collavueRedditSurcharge(dateRangeDays) : 0);
}

// -------- Daily auto-sync --------
// The `collavueRunDailyAutoSync` scheduler in functions/index.js runs at
// 04:00 UTC. Keep this hour in sync with that schedule — the UI reads it to
// tell users when their next run lands in their own time zone.
export const COLLAVUE_DAILY_SYNC_HOUR_UTC = 4;

export function nextDailySyncAt(from = new Date()) {
  const next = new Date(from);
  next.setUTCHours(COLLAVUE_DAILY_SYNC_HOUR_UTC, 0, 0, 0);
  if (next <= from) next.setUTCDate(next.getUTCDate() + 1);
  return next;
}

// Firestore timestamps arrive as Timestamp objects live and as plain
// millis/Date from cached or optimistic writes — normalize both.
function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  const ms = Number(value);
  return Number.isFinite(ms) ? new Date(ms) : null;
}

function formatWhen(date) {
  if (!date) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// Single source of truth for what the UI says about a campaign's daily sync,
// so the campaigns list and the campaign page can't drift apart. The daily
// scheduler stamps `lastAutoSyncStatus` / `lastAutoSyncAt` / `lastAutoSyncLeads`
// on every run, including the runs it skips — that's what makes "is it actually
// running?" answerable from the client.
//
// Returns { enabled, tone: "on" | "warn" | "off", label, detail, nextRunAt }.
export function dailySyncState(campaign, { credits, autoSyncEnabled } = {}) {
  const nextRunAt = nextDailySyncAt();
  const nextLabel = formatWhen(nextRunAt);

  // The account-level switch wins over every campaign's own flag — the
  // scheduler checks it first, so the UI has to as well.
  if (autoSyncEnabled === false) {
    return {
      enabled: false,
      tone: "off",
      label: "Daily sync off for your account",
      detail:
        "Daily auto-sync is turned off in Settings, so no campaign runs overnight.",
      nextRunAt: null,
    };
  }

  if (!campaign?.autoSyncDaily) {
    return {
      enabled: false,
      tone: "off",
      label: "Daily sync off",
      detail: "Turn on daily auto-sync to get fresh leads and a digest email every morning.",
      nextRunAt: null,
    };
  }

  const cost = collavueSyncCost(
    campaign.dateRangeDays,
    campaignPlatforms(campaign)
  );
  const lastRunAt = toDate(campaign.lastAutoSyncAt);
  const status = campaign.lastAutoSyncStatus || null;
  const campaignStatus = String(campaign.status || "active").toLowerCase();

  if (campaignStatus !== "active") {
    return {
      enabled: true,
      tone: "warn",
      label: "Daily sync paused",
      detail: `This campaign is ${campaignStatus}, so the daily run skips it until it's active again.`,
      nextRunAt,
    };
  }

  // Credits are checked at run time, so a balance below one run's cost means
  // tomorrow's sync will be skipped — say so before it happens.
  if (Number.isFinite(Number(credits)) && Number(credits) < cost) {
    return {
      enabled: true,
      tone: "warn",
      label: "Daily sync needs credits",
      detail: `Each run costs ${cost} credits and you have ${Number(credits)}. Top up to resume the ${nextLabel} run.`,
      nextRunAt,
    };
  }

  if (status === "skipped_credits") {
    // Credits were checked above, so reaching here with a known balance means
    // the user has already topped up — report recovery, not yesterday's skip.
    const toppedUp = Number.isFinite(Number(credits));
    return {
      enabled: true,
      tone: toppedUp ? "on" : "warn",
      label: toppedUp ? "Daily sync on" : "Daily sync skipped",
      detail: toppedUp
        ? `The ${formatWhen(lastRunAt)} run was skipped for credits — your balance covers the ${nextLabel} run.`
        : `The ${formatWhen(lastRunAt)} run was skipped — not enough credits for a ${cost}-credit sync.`,
      nextRunAt,
    };
  }

  if (status === "skipped_user_disabled") {
    return {
      enabled: true,
      tone: "warn",
      label: "Daily sync skipped",
      detail: `The ${formatWhen(lastRunAt)} run was skipped because daily auto-sync is off for your account. Turn it back on in Settings.`,
      nextRunAt,
    };
  }

  if (status === "failed") {
    return {
      enabled: true,
      tone: "warn",
      label: "Daily sync failed",
      detail: `The ${formatWhen(lastRunAt)} run didn't finish${campaign.lastAutoSyncError ? `: ${campaign.lastAutoSyncError}` : ""}. It runs again ${nextLabel}.`,
      nextRunAt,
    };
  }

  if (status === "success" && lastRunAt) {
    const found = Number(campaign.lastAutoSyncLeads ?? 0);
    return {
      enabled: true,
      tone: "on",
      label: "Daily sync on",
      detail: `Last run ${formatWhen(lastRunAt)} found ${found} new lead${found === 1 ? "" : "s"}. Next run ${nextLabel}.`,
      nextRunAt,
    };
  }

  return {
    enabled: true,
    tone: "on",
    label: "Daily sync on",
    detail: `First run ${nextLabel}. Each run costs ${cost} credits and emails you a digest.`,
    nextRunAt,
  };
}

export function categoryStyle(category) {
  switch (category) {
    case "hot":
      return "bg-rose-100 text-rose-700 border-rose-200";
    case "warm":
      return "bg-amber-100 text-amber-700 border-amber-200";
    case "research":
      return "bg-sky-100 text-sky-700 border-sky-200";
    case "competitor_complaint":
      return "bg-violet-100 text-violet-700 border-violet-200";
    default:
      return "bg-slate-100 text-slate-600 border-slate-200";
  }
}
export function categoryLabel(category) {
  switch (category) {
    case "hot":
      return "Hot lead";
    case "warm":
      return "Warm";
    case "research":
      return "Research";
    case "competitor_complaint":
      return "Competitor complaint";
    case "not_relevant":
      return "Low relevance";
    default:
      return category || "—";
  }
}

export function statusStyle(status) {
  switch (status) {
    case "saved":
      return "bg-blue-50 text-blue-700 border-blue-200";
    case "contacted":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "ignored":
      return "bg-slate-50 text-slate-500 border-slate-200";
    default:
      return "bg-white text-slate-600 border-slate-200";
  }
}
